'use client';

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import Link from 'next/link';
import './customer.css';

/* All styling lives in customer.css (scoped to .customer-page).
   Images go in public/assets/images/ .
   "Industry Scenarios" cards: har card ka image path aur link neeche `industries` array me `img` / `href` field me badlo. */

type IconName = 'users' | 'target' | 'bars' | 'trend' | 'user' | 'heart' | 'q' | 'plus' | 'doc' | 'down' | 'arrow' | 'left' | 'right' | 'check';

function Icon({ name }: { name: IconName }) {
  const p: Record<IconName, ReactNode> = {
    users: <><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><path d="M16 5a3 3 0 0 1 0 6M18 14c1.8.8 3 2.6 3 5" /></>,
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /></>,
    bars: <><path d="M5 20V12M10 20V6M15 20v-9M20 20V9" /></>,
    trend: <><path d="M4 17 10 11l4 4 6-8" /><path d="M15 7h5v5" /></>,
    user: <><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" /></>,
    heart: <><path d="M12 20s-8-5-8-11a4.5 4.5 0 0 1 8-2.8A4.5 4.5 0 0 1 20 9c0 6-8 11-8 11z" /></>,
    q: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 .9-1 1.7M12 17h.01" /></>,
    plus: <><circle cx="12" cy="12" r="9" /><path d="M12 8v8M8 12h8" /></>,
    doc: <><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5" /></>,
    down: <><path d="M12 4v12M7 11l5 5 5-5M5 20h14" /></>,
    arrow: <><path d="M5 12h14M13 6l6 6-6 6" /></>,
    left: <><path d="M15 6l-6 6 6 6" /></>,
    right: <><path d="M9 6l6 6-6 6" /></>,
    check: <><circle cx="12" cy="12" r="9" /><path d="m8 12 2.5 2.5L16 9" /></>,
  };
  return (
    <svg className="cu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {p[name]}
    </svg>
  );
}

/* ============================== DATA ============================== */

const heroChips: { icon: IconName; label: string }[] = [
  { icon: 'users', label: 'Customer Segmentation' },
  { icon: 'target', label: 'Personalised Engagement' },
  { icon: 'bars', label: 'Sales Performance' },
  { icon: 'trend', label: 'Revenue Forecasting' },
];

const strip: { icon: IconName; title: string; text: string }[] = [
  { icon: 'user', title: 'Understand Your Customers', text: '360° view across all touchpoints.' },
  { icon: 'bars', title: 'Enable Smarter Selling', text: 'Data-driven sales strategies.' },
  { icon: 'trend', title: 'Drive Higher Revenue', text: 'Identify new opportunities and reduce churn.' },
  { icon: 'heart', title: 'Create Loyal Customers', text: 'Personalised, consistent experiences.' },
];

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* Illustrative data for the dashboard tabs */
const tabs: { name: string; title: string; values: number[]; kpis: [string, string, string][] }[] = [
  { name: 'Revenue Overview', title: 'Revenue Trend', values: [40, 48, 52, 58, 62, 70, 78, 84, 92, 98, 104, 118],
    kpis: [['Total Revenue', '₹ 120M', '18%'], ['Active Customers', '12.4K', '12%'], ['Win Rate', '32%', '6%'], ['Customer Lifetime Value', '₹ 85K', '20%']] },
  { name: 'Customer Segments', title: 'High-Value Customers', values: [20, 24, 28, 33, 36, 41, 47, 52, 58, 63, 70, 76],
    kpis: [['Segments', '14', '3%'], ['High-Value Share', '22%', '5%'], ['Avg. Order Value', '₹ 9.8K', '8%'], ['Repeat Rate', '41%', '7%']] },
  { name: 'Sales Performance', title: 'Closed Deals', values: [30, 34, 33, 40, 46, 44, 52, 58, 61, 66, 72, 80],
    kpis: [['Pipeline', '₹ 310M', '14%'], ['Win Rate', '32%', '6%'], ['Sales Cycle', '38 days', '9%'], ['Quota Attainment', '94%', '4%']] },
  { name: 'Churn Risk', title: 'Customers at Risk', values: [64, 62, 60, 57, 55, 52, 50, 47, 45, 42, 40, 36],
    kpis: [['At-Risk Customers', '1.1K', '11%'], ['Churn Rate', '4.2%', '9%'], ['Saved This Year', '₹ 18M', '16%'], ['Retention', '91%', '3%']] },
  { name: 'Next Best Action', title: 'Offer Acceptance', values: [12, 15, 17, 21, 24, 27, 31, 35, 38, 43, 47, 52],
    kpis: [['Actions Served', '86K', '22%'], ['Acceptance', '18%', '5%'], ['Cross-sell Uplift', '12%', '7%'], ['Revenue Impact', '₹ 9M', '15%']] },
];

function Chart({ values, title }: { values: number[]; title: string }) {
  const W = 600, H = 220, pad = 28, max = Math.max(...values) * 1.1;
  const bw = (W - pad * 2) / values.length;
  const y = (v: number) => H - pad - (v / max) * (H - pad * 2);
  const pts = values.map((v, i) => `${pad + i * bw + bw / 2},${y(v)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${title}, monthly, illustrative`}>
      {values.map((v, i) => (
        <rect key={i} x={pad + i * bw + 6} y={y(v)} width={bw - 12} height={H - pad - y(v)} fill="#145dde" opacity=".25" />
      ))}
      <polyline points={pts} fill="none" stroke="#145dde" strokeWidth="2" />
      {values.map((v, i) => <circle key={i} cx={pad + i * bw + bw / 2} cy={y(v)} r="3" fill="#145dde" />)}
      {months.map((m, i) => (
        <text key={m} x={pad + i * bw + bw / 2} y={H - 8} fontSize="10" textAnchor="middle" fill="#64748b">{m}</text>
      ))}
    </svg>
  );
}

const questions = [
  'Which customers are most profitable?',
  'Where are we losing customers and why?',
  'Which sales opportunities should we prioritise?',
  'How can we increase cross-sell and up-sell?',
  'What will our revenue look like next quarter?',
];

/* img = full path from public/ folder (extension ke saath). Har card ke liye alag file daalo.
   href = industry page ka link (header ke Industries menu se). */
const industries: { name: string; text: string; img: string; href: string }[] = [
  {
    name: 'Retail & CPG',
    text: 'Improve customer loyalty and drive repeat purchases.',
    img: '/assets/images/Retail-&-CPG1.jpg',
    href: '/industries/fmcg-retail',
  },
  {
    name: 'Manufacturing',
    text: 'Expand key accounts and increase wallet share.',
    img: '/assets/images/manufacturing2.jpg',
    href: '/industries/manufacturing',
  },
  {
    name: 'Pharma & Healthcare',
    text: 'Enhance patient engagement and grow revenue.',
    img: '/assets/images/pharma.jpg',
    href: '/industries/pharma-lifescience',
  },
  {
    name: 'Banking & Financial Services',
    text: 'Personalise offers and improve customer retention.',
    img: '/assets/images/bfsi.jpg',
    href: '/industries/bfsi',
  },
  {
    name: 'Energy & Utilities',
    text: 'Enable targeted campaigns and optimise revenue mix.',
    img: '/assets/images/Utilities-&-Energy.jpg',
    href: '/industries/energy-utilities',
  },
];

const useCases = [
  { title: 'Customer Segmentation', text: 'Identify high-value segments using AI and behavioural data.' },
  { title: 'Churn Prediction', text: 'Predict churn risk and trigger proactive interventions.' },
  { title: 'Next Best Offer / Action', text: 'Recommend personalised offers across channels.' },
  { title: 'Sales Pipeline Intelligence', text: 'Use AI to prioritise deals and improve win rates.' },
  { title: 'Revenue Forecasting', text: 'Predict revenue using historical and external signals.' },
  { title: 'Customer Sentiment Analysis', text: 'Analyse feedback and interactions to understand sentiment.' },
];

const outcomes: { icon: IconName; text: string }[] = [
  { icon: 'user', text: 'Personalised customer experiences' },
  { icon: 'bars', text: 'Higher sales productivity' },
  { icon: 'heart', text: 'Improved retention and loyalty' },
  { icon: 'check', text: 'Data-driven revenue decisions' },
];

const problems = [
  'Inaccurate sales forecasting',
  'Low customer retention',
  'Limited visibility into customer behaviour',
  'Ineffective customer segmentation',
  'Poor cross-sell and up-sell effectiveness',
];

const RESOURCES_HREF = '/insights/playbooks-downloads';

const resources = [
  { name: 'Customer Segmentation Template', type: 'Excel' },
  { name: 'Revenue Forecasting Framework', type: 'PPT' },
  { name: 'Churn Analysis Dashboard', type: 'Excel' },
  { name: 'Next Best Offer Playbook', type: 'PDF' },
  { name: 'Sales Opportunity Scoring Template', type: 'Excel' },
];

/* ===================== SCROLL REVEAL ===================== */
/* Content stays visible if JS or IntersectionObserver is missing. */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('rv--in');
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    root.querySelectorAll<HTMLElement>('.sec-head, .head-row, .card, .uc-row, .res-row').forEach((n, i) => {
      n.classList.add('rv');
      n.style.setProperty('--rv-delay', `${(i % 5) * 70}ms`);
      io.observe(n);
    });
    return () => io.disconnect();
  }, []);
  return ref;
}

/* ============================== PAGE ============================== */

export default function Customer() {
  const ref = useReveal();
  const rail = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState(0);
  const t = tabs[tab];
  const scrollRail = (dir: number) => rail.current?.scrollBy({ left: dir * 260, behavior: 'smooth' });

  return (
    <div className="customer-page" ref={ref}>
      <main>
        {/* HERO */}
        <section className="cu-hero">
          <div className="cu-hero-bg"></div>
          <div className="cu-hero-overlay"></div>
          <div className="op-container cu-hero-inner">
            <div className="cu-hero-content">

              <h1 className="cu-hero-title">
                Customer &amp; Revenue
                <br />
                <span>Intelligence</span>
              </h1>
              <p className="cu-hero-lead">Turn customer data into measurable growth.</p>
              <p className="cu-hero-text">
                Unify customer, sales and market data to gain deeper insights, personalise experiences,
                improve sales productivity and drive sustainable revenue growth.
              </p>
              <div className="cu-actions">
                <Link className="btn-primary" href="/tellusyourproblem#tell-details">Book a Discovery Session</Link>
                <Link className="btn-outline" href="#use-cases">Explore Use Cases</Link>
              </div>
            </div>

          </div>
        </section>

        {/* VALUE STRIP */}
        <section className="op-section--slate">
          <div className="op-container">
            <div className="strip">
              {strip.map((s) => (
                <div className="strip-item" key={s.title}>
                  <span className="strip-icon"><Icon name={s.icon} /></span>
                  <div>
                    <h3 className="card-title">{s.title}</h3>
                    <p className="card-text">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FROM DATA TO GROWTH */}
        <section className="op-section op-section--white">
          <div className="op-container">
            <div className="growth">
              <div>
                <div className="sec-badge">From Data to Growth</div>
                <h2 className="sec-title">Complete view. Greater possibilities.</h2>
                <p className="sec-sub sec-sub--base">
                  Bring together data from CRM, SFA, marketing, commerce and support to understand customer
                  behaviour, predict needs and unlock new revenue opportunities.
                </p>
                <Link className="btn-primary" href="#contact">See How It Works</Link>
              </div>
              <div className="dash">
                <div className="tabs" role="tablist" aria-label="Dashboard views">
                  {tabs.map((x, i) => (
                    <button key={x.name} type="button" role="tab" className="tab" aria-selected={tab === i} onClick={() => setTab(i)}>
                      {x.name}
                    </button>
                  ))}
                </div>
                <div className="dash-body">
                  <div className="chart">
                    <div className="chart-title">{t.title} (illustrative)</div>
                    <Chart values={t.values} title={t.title} />
                  </div>
                  <div className="kpis">
                    {t.kpis.map(([label, value, delta]) => (
                      <div className="kpi" key={label}>
                        <small>{label}</small>
                        <strong>{value}<em>▲ {delta}</em></strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* KEY QUESTIONS */}
        <section className="op-section op-section--slate">
          <div className="op-container">
            <div className="head-row">
              <div className="sec-head">
                <h2 className="sec-title">Key Business Questions</h2>
                <p className="sec-sub sec-sub--base">Turn your customer and revenue questions into intelligent decisions.</p>
              </div>
              <Link className="text-link" href="#problems">View all questions</Link>
            </div>
            <div className="q-grid">
              {questions.map((q) => (
                <Link className="card q-card" href="/tellusyourproblem#tell-details" key={q}>
                  <div>
                    <span className="q-mark"><Icon name="q" /></span>
                    <p className="q-text">{q}</p>
                  </div>
                  <span className="q-go"><Icon name="arrow" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* INDUSTRIES */}
        <section className="op-section op-section--white">
          <div className="op-container">
            <div className="head-row">
              <div className="sec-head">
                <h2 className="sec-title">Industry Scenarios</h2>
                <p className="sec-sub sec-sub--base">Real-world scenarios to solve common customer and revenue challenges.</p>
              </div>
              <Link className="text-link" href="/industries">Explore industry solutions</Link>
            </div>
            <div className="rail-wrap">
              <div className="rail" ref={rail}>
                {industries.map((ind) => (
                  <Link className="card ind-card" key={ind.name} href={ind.href}>
                    <div className="ind-img" style={{ background: '#f4f8fd' }}>
                      <img src={ind.img} alt={ind.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block', maxWidth: 'none' }} />
                    </div>
                    <div className="ind-body">
                      <h3 className="card-title">{ind.name}</h3>
                      <p className="card-text">{ind.text}</p>
                    </div>
                  </Link>
                ))}
              </div>

            </div>
          </div>
        </section>

        {/* AI USE CASES */}
        <section className="op-section op-section--slate" id="use-cases">
          <div className="op-container">
            <div className="sec-head">
              <h2 className="sec-title">AI-Enabled Use Cases</h2>
              <p className="sec-sub sec-sub--base">From insight to action with applied AI.</p>
            </div>
            <div className="uc-grid">
              <div className="uc-list">
                {useCases.map((u, i) => (
                  <div className="uc-row" key={u.title}>
                    <span className="uc-num">{String(i + 1).padStart(2, '0')}</span>
                    <div className="uc-body">
                      <h3 className="uc-title">{u.title}</h3>
                      <p className="card-text">{u.text}</p>
                    </div>
                    <Link className="uc-link" href="/tellusyourproblem#tell-details">Use Case</Link>
                  </div>
                ))}
              </div>
              <div className="uc-dark">
                <small>From insights to outcomes</small>
                <h3>Drive growth with AI-powered customer intelligence.</h3>
                <ul>
                  {outcomes.map((o) => (
                    <li key={o.text}><Icon name={o.icon} />{o.text}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* PROBLEMS + RESOURCES */}
        <section className="op-section op-section--white" id="problems">
          <div className="op-container">
            <div className="split-grid">
              <div>
                <div className="sec-head">
                  <h2 className="sec-title">52 Customer &amp; Revenue Problems </h2>
                  <p className="sec-sub sec-sub--base">Real problems. Practical solutions.</p>
                </div>
                <div className="panel">
                  {problems.map((p) => (
                    <details className="acc" key={p}>
                      <summary><Icon name="plus" />{p}</summary>
                      <p className="card-text">Explore how Orane approaches this problem with your data and business rules.</p>
                    </details>
                  ))}
                </div>
              </div>
              <div>
                <div className="sec-head">
                  <h2 className="sec-title">Resources &amp; Downloads</h2>
                  <p className="sec-sub sec-sub--base">Templates, frameworks and case examples.</p>
                </div>
                <div className="panel">
                  {resources.map((r) => (
                    <Link className="res-row" href={RESOURCES_HREF} key={r.name}>
                      <span className={`res-icon res-icon--${r.type.toLowerCase()}`}><Icon name="doc" /></span>
                      <span className="res-body"><strong>{r.name}</strong><small>{r.type}</small></span>
                      <span className="res-dl" aria-label={`Download ${r.name}`}><Icon name="down" /></span>
                    </Link>
                  ))}
                </div>
                <div className="panel-foot">
                  <Link className="btn-primary" href={RESOURCES_HREF}>View All Customer &amp; Revenue Resources</Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="cu-cta" id="contact">
          <div className="cu-cta-bg"></div>
          <div className="cu-cta-overlay"></div>
          <div className="op-container cu-cta-inner">
            <div>
              <p className="cu-cta-eyebrow">Accelerate revenue. Build loyalty. Gain a complete customer view.</p>
              <h2 className="cu-cta-title">Let’s turn your customer data into measurable growth.</h2>
            </div>
            <Link className="btn-primary" href="/tellusyourproblem#tell-details">Talk to Our Team</Link>
          </div>
        </section>
      </main>
    </div>
  );
}